import MuiAppBar from "@material-ui/core/AppBar"
import IconButton from "@material-ui/core/IconButton"
import Drawer from "@material-ui/core/Drawer"
import Divider from "@material-ui/core/Divider"
import List from "@material-ui/core/List"
import Toolbar from "@material-ui/core/Toolbar"
import Tooltip from "@material-ui/core/Tooltip"
import AppMainContext from "../../AppMainContext"
import AppSubContext from "../../AppSubContext"
import InvitationsMailIcon from "./InvitationsMailIcon"
import UserMenu from "./UserMenu"
import { ChevronLeft, Menu } from "react-feather"
import React from "react"
import clsx from "clsx"
import { useMediaQuery } from "@material-ui/core"
import { useUserInvitations } from "../../../invitations/hooks/use-user-invitations"
import { useGetUnreadSummaryQuery } from "../../../chat/api"
import Logout from "../../Logout"
import AppLanguageSwitch from "../../LanguageSwitch"
import { COLOR_BLACK } from "../../../constants"
import { useStyles } from "../hooks/use-styles"
import { useTranslations } from "../hooks/use-translations"
import { useAppDrawer } from "../hooks/use-app-drawer"
import { useNavItems, type NavItem as NavItemType } from "../hooks/use-nav-items"
import NavItem from "./NavItem"
import { Redirect } from "react-router-dom"
import { useRouter } from "../../../hooks"
import { useAppContext } from "../../../app-context/use-app-context"
import { buildDefaultPathWithContext } from "../../../app-context/navigate"
import { paths } from "../../../paths"
import StarbrixLogo from "../../../assets/icons/StarbrixLogo"

const AppLayout = ({ children }: React.PropsWithChildren<{}>) => {
  const classes = useStyles()
  const { isDrawerOpen, isResourceNavOpen, openDrawer, closeDrawer, toggleResourceNav } = useAppDrawer()
  const translations = useTranslations()
  const isMobileScreen = useMediaQuery(() => "(max-width:500px)")
  const { pendingInvitations } = useUserInvitations()
  const { navItems, isNavItemsSuccess, hasHourReportsNavItem } = useNavItems()
  const { location } = useRouter()
  const { appContext } = useAppContext()
  const activeContext = appContext.subContext || appContext.mainContext
  const chatOrgId = appContext.mainContext?.type === "org" ? appContext.mainContext.id : undefined
  const { data: unreadSummary } = useGetUnreadSummaryQuery(
    { orgId: chatOrgId! },
    // chat:updated (SocketManager) is the primary refresh signal; this poll is just a
    // safety net for a missed/dropped event, so it can be slow.
    { skip: !chatOrgId, pollingInterval: 5 * 60_000 }
  )
  const unreadChatsCount = unreadSummary?.chatsWithUnreadCount ?? 0

  if (location.pathname === paths.hourReports() && isNavItemsSuccess && !hasHourReportsNavItem && activeContext) {
    const fallbackPath = buildDefaultPathWithContext({
      currentSearch: location.search,
      context: activeContext,
    })

    return <Redirect to={fallbackPath} />
  }

  const onClickNavItem = (item: NavItemType) => {
    if (item.id === "resources") {
      return toggleResourceNav()
    }

    if (isMobileScreen) {
      return closeDrawer()
    }
  }

  return (
    <div className={classes.container}>
      <MuiAppBar position="fixed" elevation={0} className={classes.appBar}>
        <Toolbar variant="dense" disableGutters>
          <div className={classes.logo}>
            <StarbrixLogo color="white" />
          </div>
          <div className={classes.appContext}>
            <AppMainContext />
            <AppSubContext />
          </div>
          {!isMobileScreen && <InvitationsMailIcon />}
          {!isMobileScreen && <UserMenu />}
        </Toolbar>
      </MuiAppBar>
      <Drawer
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: isDrawerOpen,
          [classes.drawerClose]: !isDrawerOpen,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: isDrawerOpen,
            [classes.drawerClose]: !isDrawerOpen,
          }),
        }}
      >
        <div className={classes.replacementDiv}></div>
        <div className={classes.drawerHeader}>
          <Tooltip title={isDrawerOpen ? translations.closeSidebarTip : translations.openSidebarTip} placement="right">
            <IconButton
              onClick={() => {
                isDrawerOpen ? closeDrawer() : openDrawer("onClickMenu")
              }}
            >
              {isDrawerOpen ? <ChevronLeft color={COLOR_BLACK} /> : <Menu color={COLOR_BLACK} />}
            </IconButton>
          </Tooltip>
        </div>
        <Divider />
        <div>
          <List disablePadding>
            {navItems.map((item, index) => {
              const divider = index < navItems.length - 1
              const badgeContent =
                item.id === "notifications" && pendingInvitations.length
                  ? pendingInvitations.length.toString()
                  : item.id === "chat" && unreadChatsCount
                  ? unreadChatsCount.toString()
                  : undefined

              return (
                <NavItem
                  key={item.id}
                  item={item}
                  onClick={onClickNavItem}
                  badgeContent={badgeContent}
                  tooltip={isDrawerOpen ? undefined : item.label}
                  divider={divider}
                  isOpen={isResourceNavOpen && item.id === "resources"}
                />
              )
            })}
          </List>
          {isMobileScreen && (
            <div style={{ marginTop: 24 }}>
              <Divider />
              {isDrawerOpen && (
                <div style={{ marginLeft: 12 }}>
                  <AppLanguageSwitch />
                </div>
              )}
              <Tooltip title={isDrawerOpen ? "" : "Logout"} placement="right">
                <span>
                  <Logout />
                </span>
              </Tooltip>
              <Divider />
            </div>
          )}
        </div>
      </Drawer>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: isDrawerOpen,
        })}
      >
        <div className={classes.replacementDiv}></div>
        {children}
      </main>
    </div>
  )
}

export default AppLayout
